<template>
  <div
    class="ModalYoutubeVideo"
    :class="ratio"
    @click="closeModal"
  >
    <InertiaLink
      class="ModalYoutubeVideo__logo"
      :href="route('homepage')"
      @click="closeModal"
    >
      <LogoBinalogueWhiteAvatar />
    </InertiaLink>

    <div
      data-nosnippet
      @mouseover="mouseEnter"
      @mouseleave="mouseLeave"
    >
      <div
        :id="`div${youtube}`"
        :style="{ height: videoHeight + 'px', width: videoWidth + 'px' }"
        class="ModalYoutubeVideo__container"
      />
    </div>
    <IconClose class="close" />
    <span class="cursor cursorYoutubeClose">
      <IconClose />
    </span>
  </div>
</template>

<script>
import { InertiaLink } from "@inertiajs/inertia-vue3";
import { inject } from '@vue/runtime-core';
import imageKit from "@/mixins/image-kit";

export default {
  components: {
    InertiaLink,
  },
  mixins: [imageKit],
  props: {
    youtube: {
      type: String,
      default: '',
    },
    ratio: {
      type: String,
      default: '',
    },
  },
  setup() {
    const gsap = inject('$gsap');
    const tlModalYoutubeVideo = gsap.timeline();
    const tlModalYoutubeVideoOut = gsap.timeline();
    return {
      tlModalYoutubeVideo,
      tlModalYoutubeVideoOut,
    };
  },
  data() {
    return {
      player: null,
      headerColorInit: null,
      videoWidth: 0,
      videoHeight: 0,
      plus: 9,
      divide: 16,
      scrollPosition: 0,
    };
  },
  mounted() {
    this.tlModalYoutubeVideo.to('.ModalYoutubeVideo', {
      opacity: 1,
      duration: 0.3,
      ease: "power2.inOut"
    }, 0);
    this.loadYouTubeAPI().then((YT) => {
      this.player = new YT.Player(`div${this.youtube}`, {
        videoId: this.youtube,
        playerVars: {
          autoplay: 0, // lo forzamos manualmente tras la animación
          rel: 0,
          modestbranding: 1,
          // mute: 1,
        },
        events: {
          onReady: () => {
            // ⚠️ añadimos el atributo allow al iframe
            const iframe = document.querySelector(`#div${this.youtube} iframe`);
            if (iframe) {
              iframe.setAttribute('allow', 'autoplay; encrypted-media');
            }

            // 🎬 reproducir tras la animación

            this.tlModalYoutubeVideo.to('.ModalYoutubeVideo__container', {
              scale: 1,
              duration: 0.7,
              opacity: 1,
              delay: 0.2,
              ease: "power2.out",
              onComplete: () => {
                this.player.playVideo();
              }
            });
          },
        },
      });
    });

    this.getVideoSize();

    if (!this.$store.state.isMobile) {
      this.cursorCloseAnimations();
    }

    window.addEventListener("resize", this.getVideoSize);

    setTimeout(() => {
      this.$store.commit("TOGGLE_BG_VIDEO", false);
      document.body.classList.add('horizontal');

      if (this.$store.state.isMobile) {
        this.scrollPosition = window.pageYOffset;
        document.querySelector('body').style.overflow = 'hidden';
        document.querySelector('body').style.position = 'fixed';
        document.querySelector('body').style.width = '100%';
        document.querySelector('body').style.top = `-${this.scrollPosition}px`;
      }
    }, 500);

    window.addEventListener('keyup', this.escapeVideo, false);
  },
  methods: {
    loadYouTubeAPI() {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve(window.YT);
        } else {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
          window.onYouTubeIframeAPIReady = () => {
            resolve(window.YT);
          };
        }
      });
    },

    escapeVideo(event) {
      if (event.keyCode === 27) {
        if (this.player && typeof this.player.pauseVideo === 'function') {
          this.player.pauseVideo();
        }
        this.closeModal();
      }
    },

    closeModal() {
      this.tlModalYoutubeVideo.to('.ModalYoutubeVideo__container', {
        scale: 1.4,
        opacity: 0,
        duration: 0.6,
        ease: "power2.inOut"
      });
      this.tlModalYoutubeVideoOut.to('.ModalYoutubeVideo', {
        opacity: 0,
        duration: 0.6,
        delay: 0.3,
        ease: "power4.in"
      });

      // Destruir el player
      if (this.player && typeof this.player.destroy === 'function') {
        this.player.destroy();
        this.player = null;
      }

      this.$store.commit("TOGGLE_BG_VIDEO", true);
      document.body.classList.remove('horizontal');

      if (this.$store.state.isMobile) {
        document.querySelector('body').style.removeProperty('overflow');
        document.querySelector('body').style.removeProperty('position');
        document.querySelector('body').style.removeProperty('width');
        window.scrollTo(0, this.scrollPosition);
      }

      setTimeout(() => {
        this.$store.commit("TOGGLE_IS_MODAL_YOUTUBE_VIDEO", false);
      }, 1000);
    },

    getVideoSize() {
      switch (this.ratio) {
        case "four-by-three": this.divide = 4; this.plus = 3; break;
        case "four-by-five": this.divide = 4; this.plus = 5; break;
        case "sixteen-by-five": this.divide = 16; this.plus = 5; break;
        case "sixteen-by-seven": this.divide = 16; this.plus = 7; break;
        case "two-by-one": this.divide = 2; this.plus = 1; break;
        case "square": this.divide = 1; this.plus = 1; break;
        case "sixteen-mm": this.divide = 2048; this.plus = 1240; break;
        case "nineteentwenty-by-thirteenfifty": this.divide = 1920; this.plus = 1350; break;
        case "nine-by-sixteen": this.divide = 9; this.plus = 16; break;
        case "nineteentwenty-by-sixfifty": this.divide = 1920; this.plus = 650; break;
        default: this.divide = 16; this.plus = 9;
      }

      const container = document.getElementById("div" + this.youtube);
      if (!container) return;

      const width = Math.min(window.innerWidth * 0.9, 1600); // max ancho deseado
      const height = Math.ceil((width * this.plus) / this.divide);

      this.videoWidth = width;
      this.videoHeight = height;;

      const iframe = container.querySelector('iframe');
      if (iframe) {
        iframe.setAttribute('width', this.videoWidth);
        iframe.setAttribute('height', this.videoHeight);
      }
    },

    cursorCloseAnimations() {
      this.$gsap.set(".cursorYoutubeClose", { xPercent: -50, yPercent: -50 });
      const play = document.querySelector(".cursorYoutubeClose");
      const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      const mouse = { x: pos.x, y: pos.y };
      const speed = 0.3;
      const fpms = 60 / 1000;
      const xSet = this.$gsap.quickSetter(play, "x", "px");
      const ySet = this.$gsap.quickSetter(play, "y", "px");

      window.addEventListener("mousemove", e => {
        mouse.x = e.x;
        mouse.y = e.y;
      });

      this.$gsap.ticker.add((time, deltaTime) => {
        const delta = deltaTime * fpms;
        const dt = 1.0 - Math.pow(1.0 - speed, delta);
        pos.x += (mouse.x - pos.x) * dt;
        pos.y += (mouse.y - pos.y) * dt;
        xSet(pos.x);
        ySet(pos.y);
      });
    },

    mouseEnter() {
      this.$gsap.to('.cursorYoutubeClose', { opacity: 0, duration: 0.2 });
    },

    mouseLeave() {
      this.$gsap.to('.cursorYoutubeClose', { opacity: 1, duration: 0.2 });
    },
  },
};
</script>



<style lang="scss">
.ModalYoutubeVideo {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5vh 5vw 18vh;
  // transform: scaleY(0);
  cursor: none;
  // cursor: url("/fonts/close-100.png"), auto;
  background: $black;
  opacity: 0;
  width: 100vw;
  height: 100vh;

  @include desktop-s {
    padding: 2vh 15vw;
  }

  &.four-by-three {
      padding: 5vh 5vw 18vh;

    @media (max-height: 570px) {
     padding: 1vh 25vw 2vh;
    }

    @media (max-height: 400px) {
     padding: 1vh 28vw 2vh;
    }

    @include desktop-s {
      padding: 2vh 15vw;
    }

  }

  &.nine-by-sixteen {
      padding: 5vh 5vw 5vh;

    @media (max-height: 570px) {
     padding: 1vh 25vw 2vh;
    }

    @media (max-height: 400px) {
     padding: 1vh 28vw 2vh;
    }

    @include desktop-s {
      padding: 2vh calc(50% - 20vh);
    }

  }

  &.square {
      padding: 5vh 5vw 18vh;

    @media (max-height: 570px) {
     padding: 1vh 35vw 2vh;
    }

    @media (max-height: 400px) {
     padding: 1vh 37vw 2vh;
    }

    @include desktop-s {
      padding: 2vh 27vw;
    }

  }

  @media (max-height: 570px) {
     padding: 1vh 20vw 2vh;
  }

  @media (max-height: 400px) {
     padding: 1vh 22vw 2vh;
  }

  &__container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  iframe {
    width: 100%;
    height: 100%;
    cursor: auto;
  }

  .ModalYoutubeVideo__container {
    opacity:0;
    //  background: pink;
    transform: scale(1.4);
  }

  &__logo {
    position: absolute;
    top:2px;
    left:5vw;
    margin-top: 12px;

    svg {
      height: 22px;
      width: auto;

      @include tablet {
        height: 32px;
      }
    }

    @include tablet {
      // top: 0;
      margin-top: 20px;
    }

    @include desktop-m {
      margin-top: 25px;
    }
  }

  // cursor options
  .cursorYoutubeClose {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9;
    display:none;
    pointer-events: none;
    opacity: 0;

    @include tablet {
      display: block;
    }

    svg {
      opacity:1;
      fill: $white;
      height: 40px;
      width: auto;
    }
  }

  .close {
    position: absolute;
    top:2.5vh;
    right: 6.5vw;
    color: $white;
    fill: $white;
    width: 27px;
    height: auto;

    svg {
      fill: $white;
    }

    @include tablet-m {
      display: none;

       @media (max-height: 570px) {
         top:4.5vh;
         display: block;
      }
    }
  }
}
</style>
